import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useAuthStore } from '@/store/auth';
import { useAuth } from '@/auth/AuthProvider';
import { useProjectsStore } from '@/store/projects';
import LoginModal from '@/components/auth/login-modal';
import ProjectTitle from '@/components/header/project-title';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useNav } from '@/lib/nav';
import routes from '@/lib/routes';
import { LumiLogoRound } from '@/components/icons/lumi-logo-round';
import { useCredits } from '@/hooks/useCredits';
import { authorizedFetch } from '@/lib/queryClient';
import BillingModal from '@/components/BillingModal';

export default function Header() {
  const { entitlement, signOut } = useAuthStore();
  const { user } = useAuth();
  const { currentProject } = useProjectsStore();
  const { data: credits } = useCredits(60_000);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [billingOpen, setBillingOpen] = useState(false);
  const { go } = useNav();

  async function handleUpgrade() {
    try {
      const res = await fetch('/api/config', { credentials: 'include' });
      const cfg = await res.json();
      const priceId = cfg?.stripe?.pricePlusMonthly || cfg?.priceMonthly;
      if (!priceId) return;
      const resp = await authorizedFetch('/api/billing/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ priceId })
      });
      const out = await resp.json();
      if (out?.url) {
        window.location.assign(out.url);
      }
    } catch (e) {
      // non-fatal; keep menu open
    }
  }

  function truncateEmail(email: string): string {
    try {
      const [user, domain] = email.split('@');
      if (user.length <= 6) return email;
      return `${user.slice(0, 5)}…@${domain}`;
    } catch { return email; }
  }

  return (
    <>
      <header className="bg-card border-b border-border px-6 py-4 flex items-center justify-between" data-testid="header">
        <div className="flex items-center gap-3">
          <button
            onClick={() => go(routes.home())}
            className="flex items-center gap-2"
            data-testid="logo"
            aria-label="Home"
          >
            <LumiLogoRound size={32} />
          </button>
          
          {/* Breadcrumb Navigation */}
          {currentProject && (
            <div className="hidden md:flex items-center gap-1 text-sm" data-testid="breadcrumb">
              <button
                className="text-left text-muted-foreground hover:text-foreground transition-colors"
                onClick={() => go(routes.dashboard())}
              >
                Projects
              </button>
              <ChevronRight className="h-3 w-3 text-muted-foreground" />
              <ProjectTitle editable={true} autoRename={true} />
            </div>
          )}
        </div>
        
        <div className="flex items-center gap-3">
          {user ? (
            <>
              {/* Show Upgrade button in header only when on Free plan */}
              {((credits?.plan || entitlement?.plan || 'free') === 'free') && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      onClick={handleUpgrade}
                      className="inline-flex items-center justify-center h-8 px-3 rounded-lg bg-[#ffd500] text-neutral-900 font-medium hover:bg-[#ffe34d] active:bg-[#ffd500]/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#ffd500] ring-1 ring-inset ring-black/5 whitespace-nowrap"
                    >
                      Upgrade
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>upgrade to plus plan</TooltipContent>
                </Tooltip>
              )}
              
              {/* User Menu */}
              <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus:outline-none ring-offset-background" data-testid="user-menu-trigger" aria-expanded={menuOpen} aria-haspopup="menu">
                    <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
                      <span className="text-xs font-medium text-primary-foreground">
                        {(user.email?.charAt(0).toUpperCase()) ?? '?'}
                      </span>
                    </div>
                    <ChevronDown className="h-3 w-3 text-muted-foreground" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" data-testid="user-menu" className="w-[288px] max-w-[288px] rounded-xl border-neutral-200 shadow-md">
                  <div className="p-4" style={{ ['--menu-x']: '16px', lineHeight: 1.35 } as any}>
                  {/* Header line: email + plan pill */}
                  <div className="flex items-center justify-between mb-[6px]">
                    <div className="text-sm font-semibold truncate" title={user.email ?? ''}>{truncateEmail(user.email ?? '')}</div>
                    <span className={`rounded-full border px-2 py-0.5 text-[11px] ${((credits?.plan || entitlement?.plan || 'free') === 'free') ? 'bg-neutral-100 border-neutral-200 text-neutral-700' : 'bg-neutral-50 border-neutral-200 text-neutral-800'}`}>
                      {((credits?.plan || entitlement?.plan || 'free') === 'free') ? 'FREE' : 'PLUS'}
                    </span>
                  </div>
                  {/* Credits hero with tiny progress bar */}
                  {(() => {
                    const enabled = credits?.enabled !== false;
                    const allow = Math.max(0, credits?.allowance ?? 0);
                    const remain = Math.max(0, credits?.remaining ?? 0);
                    const unlimited = credits && (credits.isUnlimited !== undefined ? credits.isUnlimited : credits.allowance >= 9999);
                    const pct = allow > 0 && !unlimited ? Math.min(100, Math.round((remain / allow) * 100)) : 0;
                    const resetText = (() => {
                      try {
                        if (!credits?.resetAt) return '';
                        return new Intl.DateTimeFormat(undefined, { day: '2-digit', month: 'short' }).format(new Date(credits.resetAt));
                      } catch { return ''; }
                    })();
                    return (
                      <div className="" aria-live="polite">
                        {!credits && (
                          <div className="space-y-[6px]">
                            <div className="h-4 w-56 bg-muted animate-pulse rounded" />
                            <div className="h-3 w-36 bg-muted animate-pulse rounded" />
                            <div className="h-1 w-56 bg-muted animate-pulse rounded" />
                          </div>
                        )}
                        {credits && (
                          <>
                            <div className="cursor-default select-none">
                              <div className="text-sm text-neutral-900">{enabled ? (unlimited ? (<span>Unlimited credits</span>) : (<span>{remain} / {allow} credits</span>)) : (<span>∞ / ∞ credits</span>)}</div>
                            </div>
                            <div className="text-[12px] leading-[1.2] text-neutral-500 mt-[2px]">Resets {resetText}</div>
                            {enabled && !unlimited && (
                              <div className="h-1.5 w-full bg-neutral-200 rounded-full overflow-hidden mt-[6px]" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct}>
                                <div className="h-full rounded-full bg-[#ffd500]" style={{ width: `${pct}%` }} />
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    );
                  })()}
                  {/* Plans & Billing - available to all users */}
                  <div className="mt-1" />
                  <DropdownMenuItem
                    className="-mx-4 px-[var(--menu-x)] py-1 h-9 text-sm text-left rounded-lg text-neutral-700 hover:text-neutral-900 hover:bg-neutral-50 data-[highlighted]:bg-neutral-50 data-[highlighted]:text-neutral-900 focus:bg-neutral-50 focus:text-neutral-900 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-neutral-200"
                    onClick={() => setBillingOpen(true)}
                  >
                    Plans & Billing
                  </DropdownMenuItem>
                  {/* Nav links */}
                  <div className="mt-1 space-y-0">
                    {/* Docs (in-app /help) */}
                    <DropdownMenuItem
                      className="-mx-4 px-[var(--menu-x)] py-1 h-9 text-sm text-left rounded-lg text-neutral-700 hover:text-neutral-900 hover:bg-neutral-50 data-[highlighted]:bg-neutral-50 data-[highlighted]:text-neutral-900 focus:bg-neutral-50 focus:text-neutral-900 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-neutral-200"
                      onClick={(e) => {
                        e.preventDefault();
                        try { if (!((import.meta as any)?.env?.PROD)) console.info('[nav]', 'internal', '/help'); } catch {}
                        go(routes.help());
                      }}
                    >
                      Docs
                    </DropdownMenuItem>
                  </div>
                  {/* Divider and sign out */}
                  <div className="my-0 h-px bg-neutral-200" role="separator" />
                  <DropdownMenuItem
                    className="-mx-4 px-[var(--menu-x)] py-1 h-9 text-sm text-left rounded-lg text-neutral-700 hover:text-neutral-900 hover:bg-neutral-50 data-[highlighted]:bg-neutral-50 data-[highlighted]:text-neutral-900 focus:bg-neutral-50 focus:text-neutral-900 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-neutral-200"
                    onClick={signOut}
                    data-testid="button-signout"
                  >
                    Sign out
                  </DropdownMenuItem>
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <Button 
              onClick={() => setShowLoginModal(true)}
              data-testid="button-login"
            >
              Sign In
            </Button>
          )}
        </div>
      </header>

      <LoginModal 
        isOpen={showLoginModal} 
        onClose={() => setShowLoginModal(false)} 
      />
      <BillingModal open={billingOpen} onOpenChange={setBillingOpen} />
    </>
  );
}
